import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Cloud, CloudOff, Upload, RefreshCw, Database, AlertCircle, CheckCircle, Info } from 'lucide-react';

import { BACKEND_URL } from '@/lib/backend';
export default function GoogleDriveBackup() {
  const [driveStatus, setDriveStatus] = useState({ connected: false, loading: true });
  const [backups, setBackups] = useState([]);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [lastBackupResult, setLastBackupResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadingBackups, setLoadingBackups] = useState(false);

  useEffect(() => {
    checkDriveStatus();
  }, []);

  const checkDriveStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/drive/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDriveStatus({ ...data, loading: false });
        if (data.connected) {
          loadBackups();
        }
      }
    } catch (err) {
      console.error('Failed to check Drive status:', err);
      setDriveStatus({ connected: false, loading: false });
    }
  };

  const connectDrive = async () => {
    try {
      setError(null);
      const response = await fetch(`${BACKEND_URL}/api/drive/connect`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to initiate Google Drive connection');
      }
    } catch (err) {
      console.error('Failed to connect Drive:', err);
      setError('Failed to connect to Google Drive. Please try again.');
    }
  };

  const disconnectDrive = async () => {
    if (!confirm('Are you sure you want to disconnect Google Drive?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/drive/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        setDriveStatus({ connected: false, loading: false });
        setBackups([]);
        setLastBackupResult(null);
      }
    } catch (err) {
      console.error('Failed to disconnect Drive:', err);
      setError('Failed to disconnect Google Drive');
    }
  };

  const createBackup = async () => {
    setBackupInProgress(true);
    setError(null);
    setLastBackupResult(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/drive/backup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastBackupResult(data);
        loadBackups();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Backup failed');
      }
    } catch (err) {
      console.error('Backup failed:', err);
      setError('Failed to create backup. Please try again.');
    } finally {
      setBackupInProgress(false);
    }
  };

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/drive/backups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      }
    } catch (err) {
      console.error('Failed to load backups:', err);
    } finally {
      setLoadingBackups(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Google Drive Backup</h1>
          <p className="text-sm text-slate-500 mt-1">Backup and restore your application data to Google Drive</p>
        </div>

        {/* Setup Instructions Alert */}
        {!driveStatus.connected && !driveStatus.loading && (
          <Alert className="mb-6 border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-sm text-slate-700">
              <strong className="font-semibold">Setup Required:</strong> To use Google Drive backup, you need to configure OAuth credentials in your backend/.env file.
              <br />
              <span className="text-xs text-slate-600 mt-1 block">
                Get credentials from: <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-sm text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Alert */}
        {lastBackupResult && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-sm text-green-700">
              <strong>Backup successful!</strong> Created {lastBackupResult.file_name} ({formatBytes(lastBackupResult.size_bytes)})
              <br />
              <span className="text-xs text-green-600">
                Backed up {lastBackupResult.total_records} records from {lastBackupResult.collections_backed_up} collections
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Connection Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {driveStatus.connected ? (
                <><Cloud className="w-5 h-5 text-green-600" /> Google Drive Connected</>
              ) : (
                <><CloudOff className="w-5 h-5 text-slate-400" /> Google Drive Not Connected</>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {driveStatus.loading ? (
              <p className="text-sm text-slate-600">Checking connection status...</p>
            ) : driveStatus.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700">Connected</Badge>
                  {driveStatus.updated_at && (
                    <span className="text-xs text-slate-500">
                      Last updated: {formatDate(driveStatus.updated_at)}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={createBackup} 
                    disabled={backupInProgress}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {backupInProgress ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating Backup...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Create Backup Now</>
                    )}
                  </Button>
                  <Button 
                    onClick={disconnectDrive} 
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <CloudOff className="w-4 h-4 mr-2" /> Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Connect your Google Drive account to enable automatic backups of all your application data.
                </p>
                <Button onClick={connectDrive} className="bg-blue-600 hover:bg-blue-700">
                  <Cloud className="w-4 h-4 mr-2" /> Connect Google Drive
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* What Gets Backed Up */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" /> What Gets Backed Up
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                'Users (All Roles)', 'Tickets', 'Work Orders', 'Inventory Items',
                'Organizations', 'Contacts', 'Password Vault', 'Devices',
                'Domains', 'SSL Certificates', 'Licenses', 'Vendors',
                'Documents & SOPs', 'Network Diagrams', 'Activity Logs'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  {item}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Backup History */}
        {driveStatus.connected && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Backup History</CardTitle>
                <Button 
                  onClick={loadBackups} 
                  variant="outline" 
                  size="sm"
                  disabled={loadingBackups}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingBackups ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingBackups ? (
                <p className="text-sm text-slate-600 text-center py-4">Loading backups...</p>
              ) : backups.length === 0 ? (
                <p className="text-sm text-slate-600 text-center py-8">
                  No backups found. Create your first backup to get started.
                </p>
              ) : (
                <div className="space-y-2">
                  {backups.map((backup) => (
                    <div 
                      key={backup.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm text-slate-800">{backup.name}</p>
                        <p className="text-xs text-slate-500">{formatDate(backup.createdTime)}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {formatBytes(backup.size)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
